import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './DemoAanvragen.css';

const STATUS_OPTIES = [
  { value: 'nieuw',          label: 'Nieuw',           kleur: '#f59e0b' },
  { value: 'gecontacteerd',  label: 'Gecontacteerd',   kleur: '#3b82f6' },
  { value: 'demo_gepland',   label: 'Demo gepland',    kleur: '#8b5cf6' },
  { value: 'gewonnen',       label: 'Gewonnen',        kleur: '#16a34a' },
  { value: 'afgewezen',      label: 'Afgewezen',       kleur: '#6b7280' },
  { value: 'spam',           label: 'Spam',            kleur: '#dc2626' }
];

const DemoAanvragen = () => {
  const { gebruiker } = useAuth();
  const navigate = useNavigate();
  const [aanvragen, setAanvragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);   // detail-modal
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (gebruiker?.rol !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    laadAanvragen();
  }, [gebruiker, navigate, filterStatus]);

  const laadAanvragen = async () => {
    setLoading(true);
    try {
      const url = '/api/forms-demo/admin' + (filterStatus ? '?status=' + filterStatus : '');
      const r = await api.get(url);
      setAanvragen(r.data.aanvragen || []);
    } catch (err) {
      console.error('Fout bij laden:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    setBusyId(id);
    try {
      await api.put('/forms-demo/admin/' + id, { status });
      await laadAanvragen();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      alert('Fout: ' + (err.response?.data?.message || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const handleNotitieSave = async () => {
    if (!selected) return;
    setBusyId(selected.id);
    try {
      await api.put('/forms-demo/admin/' + selected.id, { notitie: selected.notitie });
      await laadAanvragen();
    } catch (err) {
      alert('Fout: ' + (err.response?.data?.message || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const statusInfo = (val) => STATUS_OPTIES.find(s => s.value === val) || STATUS_OPTIES[0];

  const counts = STATUS_OPTIES.reduce((acc, s) => {
    acc[s.value] = aanvragen.filter(a => a.status === s.value).length;
    return acc;
  }, {});

  return (
    <div className="demo-aanvragen">
      <div className="da-header">
        <button className="da-btn" onClick={() => navigate('/admin')}>← Admin paneel</button>
        <div>
          <h1>📋 Demo Aanvragen</h1>
          <p className="da-sub">Aanvragen vanuit landing pages — forms.womsys.nl en womsys.nl</p>
        </div>
        <button className="da-btn" onClick={laadAanvragen}>↻ Vernieuwen</button>
      </div>

      <div className="da-filters">
        <button
          className={'da-filter-btn ' + (filterStatus === '' ? 'active' : '')}
          onClick={() => setFilterStatus('')}
        >
          Alle <span className="da-filter-count">{aanvragen.length}</span>
        </button>
        {STATUS_OPTIES.map(s => (
          <button
            key={s.value}
            className={'da-filter-btn ' + (filterStatus === s.value ? 'active' : '')}
            onClick={() => setFilterStatus(s.value)}
            style={{ '--filter-color': s.kleur }}
          >
            <span className="da-filter-dot" style={{ background: s.kleur }} />
            {s.label} <span className="da-filter-count">{counts[s.value] || 0}</span>
          </button>
        ))}
      </div>

      {loading && <div className="da-state">Laden…</div>}

      {!loading && aanvragen.length === 0 && (
        <div className="da-state da-empty">
          {filterStatus
            ? 'Geen aanvragen met deze status.'
            : 'Nog geen demo-aanvragen ontvangen.'}
        </div>
      )}

      {!loading && aanvragen.length > 0 && (
        <table className="da-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Naam</th>
              <th>Bedrijf</th>
              <th>E-mail</th>
              <th>Telefoon</th>
              <th>Bron</th>
              <th>Status</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {aanvragen.map(a => {
              const s = statusInfo(a.status);
              return (
                <tr key={a.id} className={a.status === 'nieuw' ? 'da-row-nieuw' : ''}>
                  <td className="da-cel-datum">
                    {new Date(a.created_at).toLocaleString('nl-NL', {
                      dateStyle: 'short', timeStyle: 'short'
                    })}
                  </td>
                  <td><strong>{a.naam}</strong></td>
                  <td>{a.bedrijf || '—'}</td>
                  <td>
                    <a href={'mailto:' + a.email}>{a.email}</a>
                  </td>
                  <td>
                    {a.telefoon ? <a href={'tel:' + a.telefoon}>{a.telefoon}</a> : '—'}
                  </td>
                  <td><span className="da-bron">{a.bron}</span></td>
                  <td>
                    <select
                      className="da-status-select"
                      value={a.status}
                      onChange={(e) => handleStatusChange(a.id, e.target.value)}
                      disabled={busyId === a.id}
                      style={{ borderColor: s.kleur, color: s.kleur }}
                    >
                      {STATUS_OPTIES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="da-btn da-btn-sm" onClick={() => setSelected(a)}>
                      Bekijk
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {selected && (
        <div className="da-modal-overlay">
          <div className="da-modal" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <h2>Aanvraag #{selected.id}</h2>
            <button onClick={() => setSelected(null)} style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#94a3b8" }} title="Sluiten">×</button>

            <div className="da-detail-grid">
              <div><label>Naam</label><div>{selected.naam}</div></div>
              <div><label>Bedrijf</label><div>{selected.bedrijf || '—'}</div></div>
              <div><label>E-mail</label><div><a href={'mailto:' + selected.email}>{selected.email}</a></div></div>
              <div><label>Telefoon</label><div>{selected.telefoon ? <a href={'tel:' + selected.telefoon}>{selected.telefoon}</a> : '—'}</div></div>
              <div><label>Bron</label><div>{selected.bron}</div></div>
              <div><label>Datum</label><div>{new Date(selected.created_at).toLocaleString('nl-NL')}</div></div>
            </div>

            <div className="da-detail-block">
              <label>Bericht</label>
              <div className="da-bericht">{selected.bericht || <em>geen bericht</em>}</div>
            </div>

            <div className="da-detail-block">
              <label>Notitie (intern)</label>
              <textarea
                value={selected.notitie || ''}
                onChange={(e) => setSelected({ ...selected, notitie: e.target.value })}
                placeholder="Eigen notitie — wat is er besproken, wat is de volgende stap?"
                rows={4}
              />
              <button
                className="da-btn da-btn-primary da-btn-sm"
                onClick={handleNotitieSave}
                disabled={busyId === selected.id}
                style={{ marginTop: 8 }}
              >
                {busyId === selected.id ? 'Opslaan…' : 'Notitie opslaan'}
              </button>
            </div>

            {(selected.behandeld_op || selected.behandeld_door_naam) && (
              <div className="da-meta">
                Laatst bijgewerkt door {selected.behandeld_door_naam || 'onbekend'}
                {selected.behandeld_op && ' op ' + new Date(selected.behandeld_op).toLocaleString('nl-NL')}
              </div>
            )}

            <div className="da-meta">
              <small>IP: {selected.ip_adres || '—'}</small><br/>
              <small>User-Agent: {selected.user_agent || '—'}</small>
            </div>

            <div className="da-modal-acties">
              <button className="da-btn" onClick={() => setSelected(null)}>Sluiten</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoAanvragen;
